// context/CursorContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

// Cursor context for managing cursor state across components
const CursorContext = createContext();

// Cursor provider component
export const CursorProvider = ({ children }) => {
    // Cursor variant states
    const [cursorVariant, setCursorVariant] = useState('default');
    const [cursorText, setCursorText] = useState('');
    const [isHovering, setIsHovering] = useState(false);
    const [currentSection, setCurrentSection] = useState('hero');

    // Computer showcase screen hover state
    const [isHoveringComputerScreen, setIsHoveringComputerScreen] = useState(false);
    const [hoveredScreenProject, setHoveredScreenProject] = useState(null);

    // Lamp hover state for computers showcase
    const [isHoveringLamp, setIsHoveringLamp] = useState(false);
    const [isLampOn, setIsLampOn] = useState(true);

    // Reel screen hover state
    const [isHoveringReel, setIsHoveringReel] = useState(false);

    // Mouse position with smoothing
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    // Track mouse position
    useEffect(() => {
        const mouseMove = (e) => {
            setMousePosition({
                x: e.clientX,
                y: e.clientY
            });
        };

        window.addEventListener('mousemove', mouseMove);

        return () => {
            window.removeEventListener('mousemove', mouseMove);
        };
    }, []);

    // Detect current section based on scroll position
    useEffect(() => {
        const handleScroll = () => {
            const heroSection = document.querySelector('.hero-section');
            const aboutSection = document.getElementById('about');
            const worksSection = document.getElementById('works');
            const skillsSection = document.getElementById('skills');
            const contactSection = document.getElementById('contact');

            // Get current scroll position (middle of viewport)
            const scrollPosition = window.scrollY + window.innerHeight / 2;

            // Get positions of each section
            const aboutTop = aboutSection?.getBoundingClientRect().top + window.scrollY || Infinity;
            const worksTop = worksSection?.getBoundingClientRect().top + window.scrollY || Infinity;
            const skillsTop = skillsSection?.getBoundingClientRect().top + window.scrollY || Infinity;
            const contactTop = contactSection?.getBoundingClientRect().top + window.scrollY || Infinity;

            // Determine current section
            if (scrollPosition < aboutTop) {
                setCurrentSection('hero');
            } else if (scrollPosition >= aboutTop && scrollPosition < worksTop) {
                setCurrentSection('about');
            } else if (scrollPosition >= worksTop && scrollPosition < skillsTop) {
                setCurrentSection('works');
            } else if (scrollPosition >= skillsTop && scrollPosition < contactTop) {
                setCurrentSection('skills');
            } else if (scrollPosition >= contactTop) {
                setCurrentSection('contact');
            }


        };

        // Add scroll event listener
        window.addEventListener('scroll', handleScroll);

        // Initial call to set correct section
        setTimeout(handleScroll, 500); // Delay to ensure DOM is ready

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Update cursor variant based on section
    useEffect(() => {
        // Always apply the appropriate cursor for the current section
        switch (currentSection) {
            case 'hero':
                setCursorVariant('hero');
                setCursorText('scroll to explore');
                break;
            case 'about':
                setCursorVariant('dark');
                setCursorText('');
                break;
            case 'works':
                setCursorVariant(isHovering ? 'workHover' : 'dark');
                setCursorText(isHovering ? 'see more' : '');
                break;
            case 'skills':
                setCursorVariant('dark');
                setCursorText('');
                break;
            case 'contact':
                setCursorVariant('contact');
                setCursorText('get in touch');
                break;
            default:
                // Default to dark cursor for white backgrounds when not in hero or contact
                setCursorVariant('dark');
                setCursorText('');
        }

    }, [currentSection, isHovering]);

    // Cursor context value
    const value = {
        cursorVariant,
        setCursorVariant,
        cursorText,
        setCursorText,
        isHovering,
        setIsHovering,
        currentSection,
        mousePosition,
        isHoveringComputerScreen,
        setIsHoveringComputerScreen,
        hoveredScreenProject,
        setHoveredScreenProject,
        isHoveringLamp,
        setIsHoveringLamp,
        isLampOn,
        setIsLampOn,
        isHoveringReel,
        setIsHoveringReel
    };

    return (
        <CursorContext.Provider value={value}>
            {children}
        </CursorContext.Provider>
    );
};

// Custom hook for using cursor context
export const useCursor = () => {
    const context = useContext(CursorContext);
    if (context === undefined) {
        throw new Error('useCursor must be used within a CursorProvider');
    }
    return context;
};